import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useGetTranslation } from '@travel/traveler-core/hooks';
import useDeviceType from '@travel/traveler-core/hooks/useDeviceType';
import { FlatButton } from '@travel/ui';
import LoadingCircle from '@travel/ui/components/FlatButton/loadingCircle';
import { cx, isNotEmptyArray } from '@travel/utils';

import DataLayer from 'components/DataLayer';
import Dialog from 'components/Dialog';
import ProviderFilterGroup from 'components/ProviderFilterGroup';

import {
  getFiltersContractType,
  getFiltersTotal,
  getIsFetchingFilters,
  getIsFilterSelected,
} from 'store/providerList/selectors';

import { FILTER_GROUP_IDS, PROVIDER_CONTRACT_TYPES, SORT_OPTIONS } from 'constants/provider';
import { RAT_PAGE_TYPE_OBJ } from 'constants/ratAnalytics';
import { Translate } from 'core/translate';
import type { ProviderFilter } from 'ProviderList-Types';

import type { PriceRangeType } from '../FilterContainer';
import FilterItem from '../FilterItem';

import styles from './filterAndSort.module.scss';

type Props = {
  /** on what parent page this component lives */
  onPage: 'providerListUndated' | 'providerListDated';
  /** Array of recent searched filter objects */
  filters: Array<ProviderFilter>;
  /** Flag to define whether the clear filters button should be disabled or not */
  isClearFilterButtonDisabled: boolean;
  /** Flag to define whether the dialog will be displayed or not */
  isOpen?: boolean;
  /** String of current selected min price */
  minPrice?: PriceRangeType;
  /** String of current selected max price */
  maxPrice?: PriceRangeType;
  /** Array of current selected star ratings */
  starRating?: Array<string>;
  /** Array of current selected review ratings */
  reviewRating?: Array<string>;
  /** Array of current selected feature ids */
  features?: Array<string>;
  /** String of current selected sort key */
  sortKey?: string;
  /** Function to close dialog */
  onClose: () => void;
  /** Function to trigger search with new selected condition */
  onSearch: () => void;
  /** Callback to be called when any checkbox changed */
  onCheckboxChanged: (filterGroupName: string, optionId: string) => void;
  /** Callback to be called when selected price changed */
  onPriceRangeChanged?: (min?: number, max?: number) => void;
  /** Function to clear all selected filters */
  onClearClick: () => void;
  /** total of filtered provider item */
  total?: number;
  /** boolean of current selected free cancellation*/
  freeCancellation?: boolean;
};

function FilterAndSort(props: Props) {
  const { filters, isClearFilterButtonDisabled, onPage } = props;
  const isMobile = useDeviceType() === 'sp';
  const filtersTotal = useSelector(getFiltersTotal);
  const isFetching = useSelector(getIsFetchingFilters);
  const isFilterSelected = useSelector(getIsFilterSelected);
  const contractType = useSelector(getFiltersContractType);
  const isDirectContract = contractType === PROVIDER_CONTRACT_TYPES.DIRECT;
  const isDirectContractFilterSelected = isDirectContract && isFilterSelected;

  const getTranslation = useGetTranslation();

  const sortOptions = useMemo(
    () =>
      Object.values(SORT_OPTIONS).map((option) => ({
        name: getTranslation({ id: option.labelId }),
        value: option.value,
        featureId: option.value,
        selected: option.value === props.sortKey,
        managementName: option.value,
        isObjective: false,
      })),
    [getTranslation, props.sortKey],
  );

  const { pageType, siteSection, parentName } = RAT_PAGE_TYPE_OBJ[onPage];

  const FilterAndSortContents = (
    <div className={styles.filterContainer}>
      {isMobile && (
        <>
          <div className={styles.filterSection}>
            <ProviderFilterGroup
              id={FILTER_GROUP_IDS.SORT}
              name={FILTER_GROUP_IDS.SORT}
              title={<Translate id="Provider_List.Search.Sort_Filter.Sort" />}
              options={sortOptions}
              onChange={props.onCheckboxChanged}
              type="single"
              className={styles.container}
              optionTextClassName={styles.optionTextClassName}
              shouldDisplayHits={isDirectContract}
            />
          </div>
          {filters.map((filter) => (
            // will be used for price/features/star rating filtering
            <FilterItem
              key={filter.groupId}
              filter={filter}
              minPrice={props.minPrice}
              maxPrice={props.maxPrice}
              onPriceRangeChanged={props.onPriceRangeChanged}
              features={props.features}
              onCheckboxChanged={props.onCheckboxChanged}
              starRating={props.starRating}
              reviewRating={props.reviewRating}
              freeCancellation={props.freeCancellation}
            />
          ))}
        </>
      )}

      {!isMobile && isNotEmptyArray(filters) && (
        <>
          <div className={styles.filterRow}>
            <div className={styles.fullWidthGroup}>
              <FilterItem
                filter={filters[0]}
                minPrice={props.minPrice}
                maxPrice={props.maxPrice}
                onPriceRangeChanged={props.onPriceRangeChanged}
              />
            </div>
            {filters[1] && (
              <div className={cx(styles.filterGroup, styles.fullWidthGroup)}>
                <FilterItem
                  filter={filters[1]}
                  starRating={props.starRating}
                  reviewRating={props.reviewRating}
                  features={props.features}
                  onCheckboxChanged={props.onCheckboxChanged}
                />
              </div>
            )}
          </div>
          <div className={styles.filterRow}>
            {filters.slice(2, filters.length).map((filter) => (
              <FilterItem
                key={filter.groupId}
                filter={filter}
                className={styles.filterGroup}
                starRating={props.starRating}
                reviewRating={props.reviewRating}
                features={props.features}
                onCheckboxChanged={props.onCheckboxChanged}
                freeCancellation={props.freeCancellation}
              />
            ))}
          </div>

          <div className={styles.filterActions}>
            <FlatButton
              className={styles.actionButton}
              classType="secondary"
              isDisabled={isClearFilterButtonDisabled}
              onClick={props.onClearClick}
              type="button"
              data-testid="filterAndSort-clear-flatButton"
            >
              <Translate id="Provider_List.Search.Sort_Filter.Navigation.Clear" />
            </FlatButton>
            <FlatButton
              className={styles.actionButton}
              classType="primary"
              onClick={props.onSearch}
              type="button"
              data-testid="filterAndSort-search-flatButton"
            >
              <Translate id="Traveler_Common.Search" />
            </FlatButton>
          </div>
        </>
      )}
    </div>
  );

  return (
    <div
      data-testid="filterAndSort-wrapper"
      className={props.isOpen ? styles.filterAndSortWrapper : ''}
    >
      {isMobile && props.isOpen && (
        <Dialog
          isOpen={props.isOpen}
          onClose={props.onClose}
          title={<Translate id="Provider_List.Search.Sort_Filter.Title" />}
          buttonLabelPrimary={
            <div className={styles.flatButtonContainer}>
              <Translate
                id="Provider_List.Search.Sort_Filter.Filter_PC_TL"
                className={styles.filterTotal}
              />
              {isFetching ? (
                <LoadingCircle classType="primary" />
              ) : isDirectContractFilterSelected ? (
                `(${filtersTotal})`
              ) : null}
            </div>
          }
          buttonLabelSecondary={
            <Translate id="Provider_List.Search.Sort_Filter.Navigation.Clear" />
          }
          onClickPrimary={props.onSearch}
          onClickSecondary={props.onClearClick}
          isDisabledPrimary={isFetching}
          isDisabledSecondary={isFetching || isClearFilterButtonDisabled}
          buttonPrimaryClassName={styles.customLoading}
          hasAnimation={true}
        >
          {FilterAndSortContents}
        </Dialog>
      )}

      {!isMobile && props.isOpen && <>{FilterAndSortContents}</>}
      {props.isOpen && (
        <DataLayer
          pageName={`${parentName}:sort_and_filter_hotels_form_popup`}
          pageType={pageType}
          siteSection={siteSection}
        />
      )}
    </div>
  );
}

export default FilterAndSort;
